<template>
  <section :class="sectionClass" :style="sectionStyle">
    <div v-if="bgHeroData.media" class="absolute w-full h-full left-0 top-0 z-0">
      <div
        v-if="overlayEnabled"
        :class="['absolute w-full h-full left-0 top-0 z-[1]', overlayClass]"
      />
      <ClientOnly>
        <template v-if="mediaType === 'image'">
          <NuxtImg
            class="w-full h-full object-cover"
            loading="lazy"
            :alt="bgHeroData.alt"
            :src="bgHeroData.media"
            :style="objectPositionStyle"
          />
        </template>
        <template v-else-if="mediaType === 'video'">
          <video
            v-lazy-load
            class="w-full h-full object-cover"
            loop
            muted
            autoplay
            playsinline
            :style="objectPositionStyle"
          >
            <source :src="bgHeroData.media" :alt="bgHeroData.alt" type="video/mp4" />
          </video>
        </template>
      </ClientOnly>
    </div>
    <div v-if="$slots.header" class="relative z-[1] container header">
      <slot name="header" />
    </div>
    <div class="relative" :class="[container ? 'container' : '']">
      <div v-if="title || (titleRight && titleRight.title)" class="flex mb-8 lg:mb-12">
        <Toolbar :estructure-seotitle="estructureSeotitle" :title="title" :url="url" />
        <NuxtLink
          v-if="titleRight && titleRight.title"
          :to="titleRight.link"
          class="w-24 justify-end pt-toolbar__link"
        >
          {{ titleRight.title }}
          <icon :name="titleRight.icon || 'pt-arrow-right'" class="text-xl" />
        </NuxtLink>
      </div>
      <div v-if="logo.url" class="mb-8 md:-mt-8">
        <NuxtImg
          :key="logo.url"
          :src="logo.url"
          :alt="logo.alternativeText || ''"
          width="89"
          height="41"
          loading="lazy"
        />
      </div>
      <component :is="sectionComponent" :order="order" :position="position">
        <template #content-left>
          <slot name="content-left" />
        </template>
        <template #content-right>
          <slot name="content-right" />
        </template>
        <template #content>
          <slot name="content" />
        </template>
      </component>
    </div>
  </section>
</template>

<script setup>
const componentsMap = {
  A: defineAsyncComponent(() => import('./SectionA')),
  B: defineAsyncComponent(() => import('./SectionB')),
  C: defineAsyncComponent(() => import('./SectionC')),
  D: defineAsyncComponent(() => import('./SectionD')),
  E: defineAsyncComponent(() => import('./SectionE')),
  F: defineAsyncComponent(() => import('./SectionF')),
  G: defineAsyncComponent(() => import('./SectionG')),
  H: defineAsyncComponent(() => import('./SectionH')),
  I: defineAsyncComponent(() => import('./SectionI')),
  J: defineAsyncComponent(() => import('./SectionJ')),
  K: defineAsyncComponent(() => import('./SectionK'))
};
const props = defineProps({
  order: {
    type: String,
    default: 'obverse',
    validator: value => /(reverse|obverse)/.test(value)
  },
  position: {
    type: String,
    default: 'col',
    validator: value => value.match(/(col|row)/)
  },
  grid: {
    type: String,
    default: 'A',
    validator: value => /(A|B|C|D|E|F|G|H|I|J|K)/.test(value)
  },
  overlay: {
    type: String,
    default: 'gradient',
    validator: value => /(gradient|full|hided)/.test(value)
  },
  overlayColor: {
    type: String,
    default: 'black',
    validator: value => /(black|blackTotal|blue)/.test(value)
  },
  url: { type: String, default: '' },
  title: { type: String, default: '' },
  bgAlt: { type: String, default: '' },
  logo: { type: Object, default: () => ({}) },
  container: { type: Boolean, default: true },
  bgAlternativeHash: { type: String, default: '' },
  titleRight: { type: Object, default: () => ({}) },
  estructureSeotitle: { type: Boolean, default: false },
  paddingY: { type: [String, Object], default: 'py-14' },
  height: { type: [String, Number], default: undefined },
  bgHero: { type: [Object, String], default: () => ({}) },
  backgroundPosition: { type: String, default: 'center' }
});

const sectionClass = computed(() => `relative ${props.paddingY}`);
const sectionStyle = computed(() => {
  if (props.bgHero) {
    const pixels = Number(props.height);
    const minHeight = isNaN(pixels) ? props.height : `${pixels}px`;
    return { minHeight };
  }
  return {};
});
const overlayEnabled = computed(() => props.overlay !== 'hided');
const sectionComponent = computed(() => componentsMap[props.grid]);
const objectPositionStyle = computed(() => ({ 'object-position': props.backgroundPosition }));
const overlayClass = computed(() => {
  const gradientOverlayMap = {
    black:
      'bg-[linear-gradient(0deg,#333333_43.76%,rgba(51,51,51,0)_100%)] sm:bg-[linear-gradient(90deg,#333333_43.76%,rgba(51,51,51,0)_100%)]',
    blue: 'bg-gradient-to-r from-blue-dark',
    blackTotal: 'bg-gray-strong bg-opacity-80'
  };
  return props.overlay === 'gradient'
    ? gradientOverlayMap[props.overlayColor] || ''
    : 'bg-opacity-80 bg-gray-strong';
});
const bgHeroData = computed(() => {
  const media =
    typeof props.bgHero === 'object'
      ? props.bgHero.url || props.bgHero[props.bgAlternativeHash] || ''
      : props.bgHero;
  const alt =
    typeof props.bgHero === 'object' ? props.bgHero.alternativeText || props.bgAlt : props.bgAlt;
  return { media, alt };
});
const mediaType = computed(() => {
  const media = bgHeroData.value.media;
  if (!media) return null;
  if (media.startsWith('data:image')) return 'image';

  const videoExtensions = ['mp4', 'avi', 'mov'];
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', ''];
  const match = media.match(/\.(\w+)(?:\?.*)?$/);
  const ext = match ? match[1].toLowerCase() : '';

  if (videoExtensions.includes(ext)) return 'video';
  if (imageExtensions.includes(ext)) return 'image';

  return 'file';
});
</script>
